// Snapshot undo stack. Immutable: every function returns a new History.
//
// Rules (from the reference editor, minus redo which v1 does not need):
// - Record the PRE-change document before every mutating gesture, so moves,
//   restyles, deletes and clears all undo — not just additions.
// - A continuous gesture (a drag, a resize, typing into one text element)
//   passes the same `key` on every step; only its first step records, so the
//   whole gesture is one undo entry.
// - Capped at `limit` snapshots (oldest dropped). Pure — SSR/Node safe.

export interface History<T> {
  readonly past: readonly T[];
  /** Coalescing key of the most recent record, or null when the next change must record. */
  readonly lastKey: string | null;
  readonly limit: number;
}

export const HISTORY_LIMIT = 50;

export function emptyHistory<T>(limit = HISTORY_LIMIT): History<T> {
  return { past: [], lastKey: null, limit };
}

/**
 * Record `before` (the document as it was BEFORE the change). If `key` is
 * given and equals the previous record's key, the change is part of the same
 * gesture and nothing new is recorded.
 */
export function record<T>(h: History<T>, before: T, key?: string | null): History<T> {
  if (key && h.lastKey === key) return h;
  const past = [...h.past, before];
  if (past.length > h.limit) past.splice(0, past.length - h.limit);
  return { past, lastKey: key ?? null, limit: h.limit };
}

/**
 * Drop the most recent record if it belongs to gesture `key` — used when a
 * gesture cancels itself (e.g. a new text element closed while still empty),
 * so it leaves no no-op entry behind.
 */
export function drop<T>(h: History<T>, key: string): History<T> {
  if (h.lastKey !== key || h.past.length === 0) return h;
  return { past: h.past.slice(0, -1), lastKey: null, limit: h.limit };
}

/** End any open gesture so the next change records afresh. */
export function seal<T>(h: History<T>): History<T> {
  return h.lastKey === null ? h : { ...h, lastKey: null };
}

export function canUndo<T>(h: History<T>): boolean {
  return h.past.length > 0;
}

/** Pop the newest snapshot. Returns null when there is nothing to undo. */
export function undo<T>(h: History<T>): { history: History<T>; value: T } | null {
  if (h.past.length === 0) return null;
  return {
    value: h.past[h.past.length - 1],
    history: { past: h.past.slice(0, -1), lastKey: null, limit: h.limit },
  };
}

export function clear<T>(h: History<T>): History<T> {
  return emptyHistory<T>(h.limit);
}
